import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Snackbar,
  Avatar,
  IconButton,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { getMyProfile, updateMyProfile, uploadPhoto } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import { SavingOverlay } from '../../core/public/SavingOverlay';
import { getLanguage } from '../../../app/i18n';

const ProfilePage = () => {
  const { translate, setLanguage } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    preferred_language: getLanguage(),
    password: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [photoUrl, setPhotoUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await getMyProfile();
      if (response.data) {
        setFormData({
          email: response.data.email,
          preferred_language: response.data.preferred_language || getLanguage(),
          password: '',
          newPassword: '',
          confirmPassword: '',
        });
        if (response.data.photo_url) {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
          setPhotoUrl(`${apiUrl}${response.data.photo_url}`);
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setSnackbar({
        open: true,
        message: 'Please select an image file',
        severity: 'error',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: 'Image size must be less than 5MB',
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await uploadPhoto(file);
      if (response.data) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        setPhotoUrl(`${apiUrl}${response.data.photo_url}`);
        setSnackbar({
          open: true,
          message: translate('profile.photo_uploaded'),
          severity: 'success',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (formData.newPassword && formData.newPassword.length < 6) {
      newErrors.newPassword = translate('users.password_min_length');
    }

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = translate('profile.passwords_not_match');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        preferred_language: formData.preferred_language,
      };

      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }

      await updateMyProfile(updateData);
      
      await setLanguage(formData.preferred_language, true);
      
      setSnackbar({
        open: true,
        message: translate('profile.updated_success'),
        severity: 'success',
      });

      setFormData((prev) => ({
        ...prev,
        password: '',
        newPassword: '',
        confirmPassword: '',
      }));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (email) => {
    if (!email) return 'U';
    const parts = email.split('@');
    if (parts[0].length >= 2) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  };

  if (loadingProfile) {
    return (
      <Box>
        <Typography>{translate('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Avatar
            src={photoUrl}
            sx={{ width: 120, height: 120, mb: 2 }}
          >
            {!photoUrl && getInitials(formData.email)}
          </Avatar>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="photo-upload"
            type="file"
            onChange={handlePhotoChange}
          />
          <label htmlFor="photo-upload">
            <IconButton color="primary" component="span" disabled={loading}>
              <PhotoCameraIcon />
            </IconButton>
          </label>
          <Typography variant="body2" color="textSecondary">
            {translate('profile.upload_photo')}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            margin="normal"
            label={translate('profile.email')}
            name="email"
            value={formData.email}
            disabled
          />

          <TextField
            fullWidth
            margin="normal"
            select
            label={translate('profile.language')}
            name="preferred_language"
            value={formData.preferred_language}
            onChange={handleChange}
          >
            <MenuItem value="en-US">English (US)</MenuItem>
            <MenuItem value="pt-BR">Português (BR)</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="normal"
            label={translate('profile.new_password')}
            name="newPassword"
            type="password"
            value={formData.newPassword}
            onChange={handleChange}
            error={!!errors.newPassword}
            helperText={errors.newPassword || translate('profile.password_optional')}
          />

          <TextField
            fullWidth
            margin="normal"
            label={translate('profile.confirm_password')}
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? translate('common.saving') : translate('common.save')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <SavingOverlay open={loading} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
